import React, { useState, useRef, useContext, useEffect } from 'react'
import axios from 'axios'
import {
    X, Upload, FileUp, CreditCard, Shield, CheckCircle,
    AlertTriangle, User, Calendar, Hash, ChevronRight, Loader2, CloudUpload, Tag, FolderOpen, Sparkles
} from 'lucide-react'
import { BASE_URL_API } from '../../../References/urls'
import AuthContext from '../../../store/auth-context'
import { getExecutiveFullName } from '../../../store/ExecutiveContext'
import styles from './EditPersonalInformations.module.css'

const cleanFileName = (name) =>
    name.toLowerCase().replace(/[^a-z0-9.\-_]/g, '-').replace(/-+/g, '-')

const MAX_FILE_BYTES = 700 * 1024          // 700 KB
const MAX_PDF_PAGES  = 2
const ALLOWED_TYPES  = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif', 'application/pdf'])

// Counts pages in a PDF by scanning for /Type /Page entries (excludes /Pages tree nodes).
// Returns null when the structure can't be read (compressed PDF) — caller should fail open.
const countPdfPages = (file) =>
    new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = (e) => {
            const text = e.target.result
            const matches = text.match(/\/Type\s*\/Page(?!s)/g)
            resolve(matches ? matches.length : null)
        }
        reader.onerror = () => resolve(null)
        reader.readAsBinaryString(file)
    })

const MONTH_MAP = { jan:'01', feb:'02', mar:'03', apr:'04', may:'05', jun:'06', jul:'07', aug:'08', sep:'09', oct:'10', nov:'11', dec:'12' }

const parseDateToISO = (raw) => {
    if (!raw) return null
    const s = raw.trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
    // DD/MM/YYYY or DD-MM-YYYY (with optional single-digit day/month)
    const dmy = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/)
    if (dmy) return `${dmy[3]}-${dmy[2].padStart(2,'0')}-${dmy[1].padStart(2,'0')}`
    // DD MMM YYYY  e.g. "01 Jan 1990"
    const dmmy = s.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/)
    if (dmmy) {
        const mo = MONTH_MAP[dmmy[2].toLowerCase()]
        if (mo) return `${dmmy[3]}-${mo}-${dmmy[1].padStart(2,'0')}`
    }
    return null
}

const EditPersonalInformations = ({ isVisible, onClose, selectedUser }) => {
    const authCtx = useContext(AuthContext)

    const [step, setStep] = useState('upload')
    const [docType, setDocType] = useState(null)
    const [selectedFile, setSelectedFile] = useState(null)
    const [previewUrl, setPreviewUrl] = useState(null)
    const [isDragging, setIsDragging] = useState(false)
    const [uploadPhase, setUploadPhase] = useState(null) // null | 'uploading' | 'tagging' | 'categorizing' | 'extracting'
    const [uploadProgress, setUploadProgress] = useState(0)
    const [extractedData, setExtractedData] = useState(null)
    const [uploadedDocUrl, setUploadedDocUrl] = useState(null)
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [uploadError, setUploadError] = useState(null)
    const [submitError, setSubmitError] = useState(null)
    const [validationStatus, setValidationStatus] = useState(null) // null | 'checking' | 'allowed' | 'blocked'
    const [validationMessage, setValidationMessage] = useState(null)

    const fileInputRef = useRef(null)

    useEffect(() => {
        if (!isVisible || !selectedUser?.stylist_id) return
        setValidationStatus('checking')
        setValidationMessage(null)
        fetch(BASE_URL_API + '/validatePiUpdationAllowed', {
            method: 'POST',
            body: JSON.stringify({ token: authCtx.token, stylist_id: selectedUser.stylist_id }),
        })
            .then(r => r.json())
            .then(data => {
                if (data.message === 'token Expired, please login again!') { authCtx.logout(); return }
                if (data.status === true || data.status === 'true' || data.status === 'success') {
                    setValidationStatus('allowed')
                } else {
                    setValidationStatus('blocked')
                    setValidationMessage(data.message || 'This profile cannot be updated at this time.')
                }
            })
            .catch(() => {
                setValidationStatus('blocked')
                setValidationMessage('Could not verify update eligibility. Please try again.')
            })
    }, [isVisible])

    const resetAndClose = () => {
        setStep('upload')
        setDocType(null)
        setSelectedFile(null)
        setPreviewUrl(null)
        setIsDragging(false)
        setUploadPhase(null)
        setUploadProgress(0)
        setExtractedData(null)
        setUploadedDocUrl(null)
        setShowConfirmModal(false)
        setIsSubmitting(false)
        setUploadError(null)
        setSubmitError(null)
        setValidationStatus(null)
        setValidationMessage(null)
        onClose()
    }

    const handleFileSelect = async (file) => {
        if (!file) return
        setUploadError(null)

        if (!ALLOWED_TYPES.has(file.type) && !file.type.startsWith('image/')) {
            setUploadError('Only image files (JPG, PNG, WEBP) and PDF documents are allowed.')
            return
        }

        if (file.size > MAX_FILE_BYTES) {
            const sizeMB = (file.size / (1024 * 1024)).toFixed(1)
            setUploadError(`File is too large (${sizeMB} MB). Maximum allowed size is 700 KB.`)
            return
        }

        if (file.type === 'application/pdf') {
            const pages = await countPdfPages(file)
            if (pages !== null && pages > MAX_PDF_PAGES) {
                setUploadError(`This PDF has ${pages} pages. Only PDFs with up to ${MAX_PDF_PAGES} pages are allowed.`)
                return
            }
        }

        setSelectedFile(file)
        if (file.type.startsWith('image/')) {
            setPreviewUrl(URL.createObjectURL(file))
        } else {
            setPreviewUrl(null)
        }
    }

    const handleDrop = (e) => {
        e.preventDefault()
        setIsDragging(false)
        handleFileSelect(e.dataTransfer.files[0])
    }

    const handleUploadAndExtract = async () => {
        if (!selectedFile || !docType) return
        setUploadError(null)
        setUploadProgress(0)

        try {
            // Step 1 — Upload to CDN via presigned URL
            setUploadPhase('uploading')
            const cleanedName = cleanFileName(selectedFile.name)
            const key = `uploads/stylelink-stylist-cdn/${selectedUser?.stylist_id}/${Date.now()}-${cleanedName}`

            const { data: presignedData } = await axios.get(
                'https://bucket-api.lokaci.com/generate-presigned-url-stylelink',
                {
                    params: {
                        fileName: key,
                        contentType: selectedFile.type,
                        stylistIdentifier: selectedUser?.stylist_id,
                        executiveIdentifier: authCtx?.userIdentifier,
                        userContentType: docType,
                        fileSizeBytes: selectedFile.size,
                        fileType: selectedFile.type,
                    },
                }
            )

            const { uploadURL, cloudFrontURL, currentInsertedId } = presignedData
            setUploadedDocUrl(cloudFrontURL)

            await axios.put(uploadURL, selectedFile, {
                headers: { 'Content-Type': selectedFile.type },
                onUploadProgress: (progressEvent) => {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                    setUploadProgress(percent)
                },
            })

            // Step 2 — Tag document with identity label
            setUploadPhase('tagging')
            const tagLabel = docType === 'aadhar' ? 'aadhar card' : 'pan card'
            const tagRes = await fetch(BASE_URL_API + '/updateContentTag', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    updatedBy: authCtx?.userIdentifier,
                    contentId: currentInsertedId,
                    selectedTags: JSON.stringify([tagLabel]),
                }),
            })
            const tagData = await tagRes.json()
            if (tagData.status !== 'success') {
                if (tagData.message === 'token Expired, please login again!') { authCtx.logout(); return }
                throw new Error(tagData.message || 'Tagging failed.')
            }

            // Step 3 — Set category to 'identity'
            setUploadPhase('categorizing')
            const catRes = await fetch(BASE_URL_API + '/updateRecruitContentCategory', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    id: currentInsertedId,
                    category: 'identity',
                }),
            })
            const catData = await catRes.json()
            if (catData.status !== 'success') {
                if (catData.message === 'token Expired, please login again!') { authCtx.logout(); return }
                throw new Error(catData.message || 'Category update failed.')
            }

            // Step 4 — AI extraction of personal information
            setUploadPhase('extracting')
            const extractRes = await fetch('http://54.243.255.147:3100/api/kyc/extract', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-ai-secret': 'brother-this-is-crucial-mr-chandan-ai',
                },
                body: JSON.stringify({
                    candidateId: selectedUser?.stylist_id,
                    cloudfrontUrl: cloudFrontURL,
                    invokedByType: 'ops',
                    invokedById: authCtx?.userIdentifier,
                    product: 'stylelink',
                }),
            })
            const extractData = await extractRes.json()
            if (!extractData.success) {
                throw new Error(extractData.message || 'Extraction failed.')
            }

            const aiData = extractData.data
            const aiExtracted = aiData?.extracted
            const aadhaarFront = aiExtracted?.aadhaar?.front
            const pan = aiExtracted?.pan

            const extraFields = {}
            if (aadhaarFront?.gender) extraFields['Gender'] = aadhaarFront.gender
            if (aiData?.overallConfidence != null) extraFields['Confidence'] = `${aiData.overallConfidence}%`
            if (aiData?.disposition) extraFields['Disposition'] = aiData.disposition.replace(/_/g, ' ')

            setExtractedData({
                fullname: aadhaarFront?.name || pan?.name || null,
                dateOfBirth: aadhaarFront?.dob || pan?.dob || null,
                aadharCard: aadhaarFront?.uid || null,
                pancard: pan?.number || pan?.panNumber || null,
                overallConfidence: aiData?.overallConfidence ?? null,
                extractionNotes: aiExtracted?.extractionNotes || null,
                ...(Object.keys(extraFields).length > 0 ? { extraFields } : {}),
            })
            setUploadPhase(null)
            setStep('preview')
        } catch (err) {
            setUploadError(err.message || 'Something went wrong. Please try again.')
            setUploadPhase(null)
            setUploadProgress(0)
        }
    }

    const handleSubmit = async () => {
        setIsSubmitting(true)
        setSubmitError(null)
        try {
            const dob = parseDateToISO(extractedData.dateOfBirth)

            const fields = {}
            if (extractedData.fullname) fields.stylist_name      = extractedData.fullname
            if (dob)                    fields.stylist_birthdate = dob
            if (extractedData.aadharCard) fields.uuidNumber      = extractedData.aadharCard.replace(/\s+/g, '')
            if (extractedData.pancard)  fields.panNumber         = extractedData.pancard

            const res = await fetch(BASE_URL_API + '/cpUpdateProfileField', {
                method: 'POST',
                body: JSON.stringify({
                    token:            authCtx.token,
                    stylist_id:       selectedUser?.stylist_id,
                    fields,
                    changed_by_type:  'ops',
                    changed_by_id:    authCtx?.userIdentifier,
                    changed_by_name:  getExecutiveFullName(authCtx?.userIdentifier) || authCtx?.userIdentifier,
                    change_source:    'crm_panel',
                    event_type:       'kyc_update',
                    change_reason:    'KYC fields updated via document extraction',
                }),
            })
            const data = await res.json()
            if (data.status === 'success') {
                setShowConfirmModal(false)
                setStep('confirmed')
            } else {
                if (data.message === 'token Expired, please login again!') {
                    authCtx.logout()
                } else {
                    setSubmitError(data.message || 'Update failed. Please try again.')
                }
            }
        } catch {
            setSubmitError('Network error. Please check your connection and try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    const phaseOrder = ['uploading', 'tagging', 'categorizing', 'extracting']
    const phaseIndex = phaseOrder.indexOf(uploadPhase)

    const getStepStatus = (phase) => {
        const idx = phaseOrder.indexOf(phase)
        if (idx < phaseIndex) return 'done'
        if (idx === phaseIndex) return 'active'
        return 'pending'
    }

    if (!isVisible) return null

    return (
        <>
            <div className={styles.backdrop} onClick={resetAndClose} />

            <div className={`${styles.panel} ${isVisible ? styles.panelVisible : ''}`}>

                {/* ── Header ── */}
                <div className={styles.panelHeader}>
                    <div className={styles.headerLeft}>
                        <div className={styles.headerIconCircle}>
                            <Shield size={18} strokeWidth={2.2} />
                        </div>
                        <div>
                            <h2 className={styles.headerTitle}>Personal Information</h2>
                            <p className={styles.headerSubtitle}>Verified via government-issued document</p>
                        </div>
                    </div>
                    <button className={styles.closeBtn} onClick={resetAndClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* ── Progress bar — only when update is allowed ── */}
                {validationStatus === 'allowed' && <div className={styles.progressBar}>
                    <div className={`${styles.progressStep} ${step !== 'confirmed' ? styles.progressStepActive : styles.progressStepDone}`}>
                        <div className={styles.progressCircle}>
                            {step === 'preview' || step === 'confirmed' ? <CheckCircle size={14} /> : '1'}
                        </div>
                        <span>Upload</span>
                    </div>
                    <div className={`${styles.progressLine} ${(step === 'preview' || step === 'confirmed') ? styles.progressLineFilled : ''}`} />
                    <div className={`${styles.progressStep} ${step === 'preview' ? styles.progressStepActive : step === 'confirmed' ? styles.progressStepDone : styles.progressStepMuted}`}>
                        <div className={styles.progressCircle}>
                            {step === 'confirmed' ? <CheckCircle size={14} /> : '2'}
                        </div>
                        <span>Review</span>
                    </div>
                    <div className={`${styles.progressLine} ${step === 'confirmed' ? styles.progressLineFilled : ''}`} />
                    <div className={`${styles.progressStep} ${step === 'confirmed' ? styles.progressStepDone : styles.progressStepMuted}`}>
                        <div className={styles.progressCircle}>
                            {step === 'confirmed' ? <CheckCircle size={14} /> : '3'}
                        </div>
                        <span>Confirmed</span>
                    </div>
                </div>}

                {/* ── Body ── */}
                <div className={styles.panelBody}>

                    {/* VALIDATING */}
                    {(validationStatus === null || validationStatus === 'checking') && (
                        <div className={styles.validatingContent}>
                            <Loader2 size={34} className={styles.spin} color="#ccc" />
                            <p className={styles.validatingText}>Checking eligibility…</p>
                        </div>
                    )}

                    {/* BLOCKED */}
                    {validationStatus === 'blocked' && (
                        <div className={styles.blockedContent}>
                            <div className={styles.blockedIconCircle}>
                                <AlertTriangle size={44} strokeWidth={1.5} />
                            </div>
                            <h3 className={styles.blockedTitle}>Update Not Allowed</h3>
                            <p className={styles.blockedMessage}>{validationMessage}</p>
                            <button className={styles.ghostBtn} onClick={resetAndClose}>Close Panel</button>
                        </div>
                    )}

                    {/* STEP 1 — UPLOAD */}
                    {validationStatus === 'allowed' && step === 'upload' && (
                        <div className={styles.stepContent}>
                            <div className={styles.stepIntro}>
                                <h3 className={styles.stepTitle}>Choose a document to upload</h3>
                                <p className={styles.stepSubtitle}>We'll extract the personal details automatically.</p>
                            </div>

                            <div className={styles.docTypeRow}>
                                <button
                                    className={`${styles.docTypeCard} ${docType === 'pancard' ? styles.docTypeCardActive : ''}`}
                                    onClick={() => { setDocType('pancard'); setSelectedFile(null); setPreviewUrl(null) }}
                                    disabled={uploadPhase !== null}
                                >
                                    {docType === 'pancard' && <div className={styles.selectedBadge}><CheckCircle size={14} /></div>}
                                    <CreditCard size={30} strokeWidth={1.8} />
                                    <strong>PAN Card</strong>
                                    <small>10-character alphanumeric</small>
                                </button>

                                <button
                                    className={`${styles.docTypeCard} ${docType === 'aadhar' ? styles.docTypeCardActive : ''}`}
                                    onClick={() => { setDocType('aadhar'); setSelectedFile(null); setPreviewUrl(null) }}
                                    disabled={uploadPhase !== null}
                                >
                                    {docType === 'aadhar' && <div className={styles.selectedBadge}><CheckCircle size={14} /></div>}
                                    <Shield size={30} strokeWidth={1.8} />
                                    <strong>Aadhar Card</strong>
                                    <small>12-digit unique identity</small>
                                </button>
                            </div>

                            {docType && (
                                <div className={styles.uploadSection}>
                                    {/* Processing steps overlay — shown while running */}
                                    {uploadPhase !== null ? (
                                        <div className={styles.processingContainer}>
                                            <p className={styles.processingTitle}>Processing your document…</p>

                                            <div className={styles.processingSteps}>
                                                {/* Upload step */}
                                                <div className={`${styles.processingStep} ${styles[`processingStep_${getStepStatus('uploading')}`]}`}>
                                                    <div className={styles.processingStepIcon}>
                                                        {getStepStatus('uploading') === 'done'
                                                            ? <CheckCircle size={18} />
                                                            : getStepStatus('uploading') === 'active'
                                                                ? <Loader2 size={18} className={styles.spin} />
                                                                : <CloudUpload size={18} />}
                                                    </div>
                                                    <div className={styles.processingStepBody}>
                                                        <span className={styles.processingStepLabel}>Uploading document to CDN</span>
                                                        {getStepStatus('uploading') === 'active' && (
                                                            <div className={styles.uploadProgressWrap}>
                                                                <div className={styles.uploadProgressBar}>
                                                                    <div
                                                                        className={styles.uploadProgressFill}
                                                                        style={{ width: `${uploadProgress}%` }}
                                                                    />
                                                                </div>
                                                                <span className={styles.uploadProgressPct}>{uploadProgress}%</span>
                                                            </div>
                                                        )}
                                                        {getStepStatus('uploading') === 'done' && (
                                                            <span className={styles.processingStepDoneLabel}>Uploaded successfully</span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className={styles.processingConnector} />

                                                {/* Tag step */}
                                                <div className={`${styles.processingStep} ${styles[`processingStep_${getStepStatus('tagging')}`]}`}>
                                                    <div className={styles.processingStepIcon}>
                                                        {getStepStatus('tagging') === 'done'
                                                            ? <CheckCircle size={18} />
                                                            : getStepStatus('tagging') === 'active'
                                                                ? <Loader2 size={18} className={styles.spin} />
                                                                : <Tag size={18} />}
                                                    </div>
                                                    <div className={styles.processingStepBody}>
                                                        <span className={styles.processingStepLabel}>Tagging as personal identity document</span>
                                                        {getStepStatus('tagging') === 'active' && (
                                                            <span className={styles.processingStepHint}>Linking document to profile…</span>
                                                        )}
                                                        {getStepStatus('tagging') === 'done' && (
                                                            <span className={styles.processingStepDoneLabel}>Tagged successfully</span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className={styles.processingConnector} />

                                                {/* Categorize step */}
                                                <div className={`${styles.processingStep} ${styles[`processingStep_${getStepStatus('categorizing')}`]}`}>
                                                    <div className={styles.processingStepIcon}>
                                                        {getStepStatus('categorizing') === 'done'
                                                            ? <CheckCircle size={18} />
                                                            : getStepStatus('categorizing') === 'active'
                                                                ? <Loader2 size={18} className={styles.spin} />
                                                                : <FolderOpen size={18} />}
                                                    </div>
                                                    <div className={styles.processingStepBody}>
                                                        <span className={styles.processingStepLabel}>Categorizing as identity document</span>
                                                        {getStepStatus('categorizing') === 'active' && (
                                                            <span className={styles.processingStepHint}>Setting category to identity…</span>
                                                        )}
                                                        {getStepStatus('categorizing') === 'done' && (
                                                            <span className={styles.processingStepDoneLabel}>Categorized successfully</span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className={styles.processingConnector} />

                                                {/* Extract step */}
                                                <div className={`${styles.processingStep} ${styles[`processingStep_${getStepStatus('extracting')}`]}`}>
                                                    <div className={styles.processingStepIcon}>
                                                        {getStepStatus('extracting') === 'done'
                                                            ? <CheckCircle size={18} />
                                                            : getStepStatus('extracting') === 'active'
                                                                ? <Loader2 size={18} className={styles.spin} />
                                                                : <Sparkles size={18} />}
                                                    </div>
                                                    <div className={styles.processingStepBody}>
                                                        <span className={styles.processingStepLabel}>Extracting personal information</span>
                                                        {getStepStatus('extracting') === 'active' && (
                                                            <span className={styles.processingStepHint}>Reading fields from document…</span>
                                                        )}
                                                        {getStepStatus('extracting') === 'done' && (
                                                            <span className={styles.processingStepDoneLabel}>Extraction complete</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <p className={styles.uploadLabel}>
                                                Upload {docType === 'pancard' ? 'PAN Card' : 'Aadhar Card'} image or PDF
                                            </p>

                                            <div
                                                className={`${styles.dropZone} ${isDragging ? styles.dropZoneDragging : ''} ${selectedFile ? styles.dropZoneHasFile : ''}`}
                                                onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
                                                onDragLeave={() => setIsDragging(false)}
                                                onDrop={handleDrop}
                                                onClick={() => fileInputRef.current?.click()}
                                            >
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept="image/*,.pdf"
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => handleFileSelect(e.target.files[0])}
                                                />

                                                {selectedFile ? (
                                                    <div className={styles.filePreviewInner}>
                                                        {previewUrl
                                                            ? <img src={previewUrl} alt="preview" className={styles.docPreviewImg} />
                                                            : <div className={styles.pdfPlaceholder}><FileUp size={40} strokeWidth={1.4} /><span>{selectedFile.name}</span></div>
                                                        }
                                                        <div className={styles.fileInfoRow}>
                                                            <CheckCircle size={15} color="#22c55e" />
                                                            <span className={styles.fileName}>{selectedFile.name}</span>
                                                            <button
                                                                className={styles.changeLinkBtn}
                                                                onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setPreviewUrl(null) }}
                                                            >
                                                                Change
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className={styles.dropZoneIdle}>
                                                        <div className={styles.uploadIconCircle}>
                                                            <Upload size={26} strokeWidth={1.8} />
                                                        </div>
                                                        <p className={styles.dropTitle}>Drag &amp; drop here, or click to browse</p>
                                                        <p className={styles.dropSubtitle}>JPG, PNG, WEBP, PDF &nbsp;·&nbsp; Max 700 KB &nbsp;·&nbsp; PDF max 2 pages</p>
                                                    </div>
                                                )}
                                            </div>

                                            {uploadError && (
                                                <div className={styles.errorBanner}>
                                                    <AlertTriangle size={15} />
                                                    <span>{uploadError}</span>
                                                </div>
                                            )}

                                            <button
                                                className={styles.primaryBtn}
                                                onClick={handleUploadAndExtract}
                                                disabled={!selectedFile}
                                            >
                                                Upload &amp; Extract <ChevronRight size={17} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* STEP 2 — PREVIEW */}
                    {validationStatus === 'allowed' && step === 'preview' && extractedData && (
                        <div className={styles.previewStepContent}>
                            {extractedData.overallConfidence === 0 ? (
                                <div className={styles.previewBadgeLow}>
                                    <AlertTriangle size={20} />
                                    <span>Low confidence — the document may not be a valid KYC document. Review the notes below before submitting.</span>
                                </div>
                            ) : (
                                <div className={styles.previewBadge}>
                                    <CheckCircle size={22} color="#22c55e" />
                                    <span>Extraction successful — compare the document with the extracted values below</span>
                                </div>
                            )}

                            <div className={styles.reviewLayout}>
                                {/* Left — uploaded document */}
                                <div className={styles.reviewDocPane}>
                                    <div className={styles.reviewDocHeader}>
                                        <h3 className={styles.reviewDocTitle}>Uploaded Document</h3>
                                        <p className={styles.reviewDocSubtitle}>
                                            {docType === 'pancard' ? 'PAN Card' : 'Aadhar Card'}&nbsp;·&nbsp;{selectedFile?.name}
                                        </p>
                                    </div>
                                    <div className={styles.reviewDocViewer}>
                                        {previewUrl ? (
                                            <img src={previewUrl} alt="Uploaded document" className={styles.reviewDocImg} />
                                        ) : uploadedDocUrl ? (
                                            <iframe
                                                src={uploadedDocUrl}
                                                title="Uploaded document"
                                                className={styles.reviewDocPdf}
                                            />
                                        ) : (
                                            <div className={styles.reviewDocFallback}>
                                                <FileUp size={48} strokeWidth={1.4} />
                                                <span>{selectedFile?.name}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Right — extracted fields */}
                                <div className={styles.reviewDataPane}>
                                    <div className={styles.stepIntro}>
                                        <h3 className={styles.stepTitle}>Extracted Information</h3>
                                        <p className={styles.stepSubtitle}>These fields are read-only. Verify before submitting.</p>
                                    </div>

                                    <div className={styles.extractedList}>
                                        {extractedData.fullname && (
                                            <div className={styles.extractedRow}>
                                                <div className={styles.extractedIcon}><User size={16} strokeWidth={2} /></div>
                                                <div className={styles.extractedContent}>
                                                    <label>Full Name</label>
                                                    <span>{extractedData.fullname}</span>
                                                </div>
                                            </div>
                                        )}
                                        {extractedData.dateOfBirth && (
                                            <div className={styles.extractedRow}>
                                                <div className={styles.extractedIcon}><Calendar size={16} strokeWidth={2} /></div>
                                                <div className={styles.extractedContent}>
                                                    <label>Date of Birth</label>
                                                    <span>{extractedData.dateOfBirth}</span>
                                                </div>
                                            </div>
                                        )}
                                        {extractedData.pancard && (
                                            <div className={styles.extractedRow}>
                                                <div className={styles.extractedIcon}><CreditCard size={16} strokeWidth={2} /></div>
                                                <div className={styles.extractedContent}>
                                                    <label>PAN Card Number</label>
                                                    <span className={styles.mono}>{extractedData.pancard}</span>
                                                </div>
                                            </div>
                                        )}
                                        {extractedData.aadharCard && (
                                            <div className={styles.extractedRow}>
                                                <div className={styles.extractedIcon}><Shield size={16} strokeWidth={2} /></div>
                                                <div className={styles.extractedContent}>
                                                    <label>Aadhar Card Number</label>
                                                    <span className={styles.mono}>{extractedData.aadharCard}</span>
                                                </div>
                                            </div>
                                        )}
                                        {extractedData.extraFields && Object.entries(extractedData.extraFields).map(([k, v]) => (
                                            <div key={k} className={styles.extractedRow}>
                                                <div className={styles.extractedIcon}><Hash size={16} strokeWidth={2} /></div>
                                                <div className={styles.extractedContent}>
                                                    <label>{k}</label>
                                                    <span>{v}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {extractedData.extractionNotes && (
                                        <div className={`${styles.extractionNotesCard} ${extractedData.overallConfidence === 0 ? styles.extractionNotesCardLow : ''}`}>
                                            <p className={styles.extractionNotesLabel}>AI Extraction Notes</p>
                                            <p className={styles.extractionNotesText}>{extractedData.extractionNotes}</p>
                                        </div>
                                    )}

                                    <div className={styles.warningCard}>
                                        <AlertTriangle size={18} strokeWidth={2} />
                                        <div>
                                            <strong>One-time update</strong>
                                            <p>Once submitted, this information is locked permanently and cannot be changed.</p>
                                        </div>
                                    </div>

                                    <div className={styles.actionRow}>
                                        <button className={styles.ghostBtn} onClick={() => setStep('upload')}>← Go Back</button>
                                        <button
                                            className={styles.primaryBtn}
                                            onClick={() => setShowConfirmModal(true)}
                                            disabled={extractedData.overallConfidence === 0}
                                        >
                                            Submit &amp; Update <ChevronRight size={17} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 3 — CONFIRMED */}
                    {validationStatus === 'allowed' && step === 'confirmed' && (
                        <div className={styles.confirmedContent}>
                            <div className={styles.successCircle}>
                                <CheckCircle size={52} strokeWidth={1.5} />
                            </div>
                            <h3 className={styles.confirmedTitle}>All Done!</h3>
                            <p className={styles.confirmedSubtitle}>Personal information has been verified and permanently saved.</p>
                            <button className={styles.primaryBtn} style={{ marginTop: 32 }} onClick={resetAndClose}>
                                Close Panel
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Confirmation Modal ── */}
            {showConfirmModal && (
                <div className={styles.modalBackdrop}>
                    <div className={styles.confirmModal}>
                        <div className={styles.modalTopIcon}>
                            <AlertTriangle size={28} strokeWidth={2} />
                        </div>
                        <h3 className={styles.modalTitle}>Are you absolutely sure?</h3>
                        <p className={styles.modalBody}>
                            You're about to permanently update this user's personal information.
                            <br /><br />
                            <strong>This is a one-time action and cannot be reversed or updated in the future.</strong>
                        </p>
                        {submitError && (
                            <div className={styles.modalError}>
                                <AlertTriangle size={14} />
                                <span>{submitError}</span>
                            </div>
                        )}
                        <div className={styles.modalActions}>
                            <button className={styles.ghostBtn} onClick={() => { setShowConfirmModal(false); setSubmitError(null) }} disabled={isSubmitting}>
                                Cancel
                            </button>
                            <button className={styles.confirmBtn} onClick={handleSubmit} disabled={isSubmitting}>
                                {isSubmitting
                                    ? <><Loader2 size={16} className={styles.spin} /> Saving…</>
                                    : 'Yes, Update Permanently'
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default EditPersonalInformations
